import {
  Button,
  Center,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { CapacityAllocationWorkStatus } from "@src/__generated__/urql-graphql";
import type {
  PlanningDailyViewUserByDayModel,
  PlanningDailyViewUserDayModel,
} from "@src/components/modules/resource-planning/timeline/models";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import {
  CAPACITY_STRIP_HEIGHT,
  COLUMN_HEADER_HEIGHT,
} from "@src/constants/planning";
import { can, cannot } from "@src/utils/components/permissions";
import { getUsersPlannableCapacityForDate } from "@src/utils/getUsersPlannableCapacity";
import { useStore } from "@src/utils/hooks";
import {
  secondsToHours,
  secondsToHoursWithMinutes,
  secondsToTime,
} from "@src/utils/time";
import type { MathSign } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useCallback, useMemo } from "react";

interface UserCapacityStripForDayProps {
  user: PlanningDailyViewUserByDayModel["user"];
  day: PlanningDailyViewUserByDayModel["items"][0];
  capacity: PlanningDailyViewUserDayModel["capacity"];
  workStatus: PlanningDailyViewUserDayModel["workStatus"];
}

enum CapacityStripState {
  Free = "FREE",
  Over = "OVER",
  Full = "FULL",
  OFF = "OFF",
  NO_CAPACITY = "NO_CAPACITY",
  UNAVAILABLE = "UNAVAILABLE",
}

export const formatCapacity = (
  capacity: number | null | undefined,
  workStatus: PlanningDailyViewUserDayModel["workStatus"],
): string => {
  if (!capacity || workStatus !== CapacityAllocationWorkStatus.Working)
    return "";
  return secondsToHoursWithMinutes(capacity);
};

export const getCapacityStripState = (
  workStatus: PlanningDailyViewUserDayModel["workStatus"],
  capacity: PlanningDailyViewUserDayModel["capacity"],
): CapacityStripState => {
  switch (workStatus) {
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: Every case is handled by return so there is no fallthrough
    case CapacityAllocationWorkStatus.Working:
      switch (Math.sign(secondsToHours(capacity ?? 0)) as MathSign) {
        case 1:
          return CapacityStripState.Free;
        case -1:
          return CapacityStripState.Over;
        case 0:
          return CapacityStripState.Full;
      }
    case CapacityAllocationWorkStatus.TimeOff:
      return CapacityStripState.OFF;
    case CapacityAllocationWorkStatus.WithoutCapacity:
      return CapacityStripState.NO_CAPACITY;
    case CapacityAllocationWorkStatus.Unavailable:
      return CapacityStripState.UNAVAILABLE;
    default:
      return CapacityStripState.NO_CAPACITY;
  }
};

const colors: Record<
  CapacityStripState,
  {
    bg: string;
    foreground: string;
  }
> = {
  [CapacityStripState.Free]: { bg: "jade.100", foreground: "jade.800" },
  [CapacityStripState.Full]: { bg: "jade.500", foreground: "white" },
  [CapacityStripState.Over]: { bg: "red.500", foreground: "white" },
  [CapacityStripState.OFF]: { bg: "grey.500", foreground: "white" },
  [CapacityStripState.NO_CAPACITY]: { bg: "grey.500", foreground: "white" },
  [CapacityStripState.UNAVAILABLE]: { bg: "grey.500", foreground: "white" },
};

export const getCapacityTexts = (): Record<CapacityStripState, string> => ({
  [CapacityStripState.Free]: t`free`,
  [CapacityStripState.Full]: t`Full`,
  [CapacityStripState.Over]: t`over`,
  [CapacityStripState.OFF]: t`Off`,
  [CapacityStripState.NO_CAPACITY]: t`No capacity`,
  [CapacityStripState.UNAVAILABLE]: t`Unavailable`,
});

export const UserCapacityStripForDay: FunctionComponent<UserCapacityStripForDayProps> =
  observer(function UserCapacityStripForDay({
    user,
    day,
    capacity,
    workStatus,
  }) {
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const { allocationModalStore, trackedForDayModalStore, workspaceStore } =
      useStore();
    const state = getCapacityStripState(workStatus, capacity);
    const text = getCapacityTexts()[state];
    const { bg, foreground } = colors[state];
    const hideForCreatives =
      workspaceStore.settings?.hide_planning_total_daily_capacity &&
      cannot("planning_show_daily_capacity");

    const capacityText = useMemo(() => {
      return `${
        state !== CapacityStripState.Full && state !== CapacityStripState.OFF
          ? `${formatCapacity(capacity, workStatus)} `
          : ""
      }${text}`;
    }, [state, capacity, workStatus, text]);

    const allowRender = useMemo(() => {
      if (
        state === CapacityStripState.Free ||
        state === CapacityStripState.Over ||
        state === CapacityStripState.OFF
      )
        return false;
      return (
        can("planning_create_all") ||
        can({ do: "planning_create_own", eval: ({ id }) => id === user.id })
      );
    }, [user.id, state]);

    const plannableCapacity = useMemo(() => {
      return secondsToTime(
        getUsersPlannableCapacityForDate(
          user.profile?.planningRanges ?? [],
          new Date(day.date),
        ),
        false,
      );
    }, [user.profile?.planningRanges, day.date]);

    const totalTrackedForDay = useMemo(() => {
      return secondsToTime(day.total_tracked_for_date, false);
    }, [day.total_tracked_for_date]);

    const totalTrackedTimeText = useMemo(() => {
      const bonus =
        workspaceStore.settings?.hide_planning_total_daily_capacity &&
        cannot("planning_show_daily_capacity")
          ? ""
          : ` / ${plannableCapacity}`;

      return totalTrackedForDay + bonus;
    }, [
      day.total_tracked_for_date,
      user.profile?.planningRanges,
      workspaceStore.settings?.hide_planning_total_daily_capacity,
    ]);

    const handleTotalTimeTrackedClick = useCallback(() => {
      trackedForDayModalStore.modalState.onOpen({
        userId: user.id,
        date: new Date(day.date),
      });
    }, [user.id, day.date]);

    const handleAddCapacityAllocationClick = useCallback(() => {
      allocationModalStore.modalState.onOpen({
        userId: user.id,
        date: new Date(day.date),
        onSubmit: () =>
          resourcePlanningStore.fetchDailyView({
            loadForAllUsers: true,
            backgroundUpdate: false,
          }),
      });
    }, [user.id, day.date]);

    return (
      <HStack
        pos="sticky"
        zIndex="10"
        top={`calc(${COLUMN_HEADER_HEIGHT} - 1px)`}
        align="center"
        justify="space-between"
        h={CAPACITY_STRIP_HEIGHT}
        pl="2"
        bg={hideForCreatives ? "jade.100" : bg}
      >
        {hideForCreatives ? (
          <>
            <Button
              color="jade.800"
              textAlign="left"
              whiteSpace="break-spaces"
              onClick={handleAddCapacityAllocationClick}
              size="xs"
              variant="plain"
            >
              <Trans>Add capacity allocation</Trans>
            </Button>
            {!!totalTrackedTimeText && (
              <Button
                color="jade.800"
                leftIcon={<TrackedTimeIcon />}
                onClick={handleTotalTimeTrackedClick}
                size="xs"
                variant="plain"
              >
                {totalTrackedTimeText}
              </Button>
            )}
          </>
        ) : state === CapacityStripState.Full ? (
          <Tooltip
            label={
              <Text>
                <Trans>Total tracked time:</Trans>&nbsp;
                {totalTrackedForDay}
                <br />
                <Trans>Daily capacity:</Trans>&nbsp;
                {plannableCapacity}
              </Text>
            }
            placement="top"
            shouldWrapChildren
          >
            <Button
              color={foreground}
              onClick={handleTotalTimeTrackedClick}
              size="sm"
              variant="plain"
            >
              {capacityText}
            </Button>
          </Tooltip>
        ) : (
          <>
            <Text color={foreground} fontSize="xs" fontWeight="bold">
              {capacityText}
            </Text>
            {!!totalTrackedTimeText &&
              state !== CapacityStripState.UNAVAILABLE && (
                // <Center pos="absolute" right="8" left="8">
                <Center>
                  <Button
                    color={foreground}
                    leftIcon={<TrackedTimeIcon />}
                    onClick={handleTotalTimeTrackedClick}
                    size="xs"
                    variant="plain"
                  >
                    {totalTrackedTimeText}
                  </Button>
                </Center>
              )}
          </>
        )}
        {allowRender && (
          <Menu>
            <MenuButton>
              <IconButton
                color={hideForCreatives ? "jade.800" : foreground}
                aria-label={t`User day options toggle`}
                icon={<Icon boxSize="4" name="dots-vertical" />}
                size="xs"
                variant="plain"
              />
            </MenuButton>
            <MenuList>
              <MenuItem onClick={handleAddCapacityAllocationClick}>
                <Trans>Allocate capacity</Trans>
              </MenuItem>
              <MenuItem onClick={handleTotalTimeTrackedClick}>
                <Trans>Show total tracked time</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
    );
  });
